/**
 * Footer
 *
 * Marketing site footer with product/company links, social icons, and smooth
 * scroll handlers for same-page sections.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Youtube } from 'lucide-react';
import useDarkMode from '@/hooks/useDarkMode';

const navigation = {
  product: [
    { name: 'Templates', href: '/demo-templates' },
    { name: 'Done For You', href: '/done-for-you' },
    { name: 'Pricing', href: '#pricing' },
  ],
  company: [
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '#contact' },
    { name: 'Terms', href: '/tos' },
    { name: 'Privacy', href: '/privacy' },
    { name: 'Acceptable Use', href: '/aup' },
    { name: 'Data Processing', href: '/dpa' },
  ],
  social: [
    { name: 'Instagram', href: 'https://www.instagram.com/advocacy.lab?igsh=dnQxOGY4aG94aTlk&utm_source=qr', icon: Instagram },
    { name: 'Twitter', href: 'https://x.com/AdvocacyLabio', icon: Twitter }, 
    { name: 'YouTube', href: 'https://www.youtube.com/@Advocacy_Lab', icon: Youtube },
  ],
};

export function Footer() {
  const isDarkMode = useDarkMode();
  // Maintain local state to ensure rendering is consistent
  const [isDark, setIsDark] = useState(isDarkMode);
  
  // Update local state whenever the hook value changes
  useEffect(() => {
    setIsDark(isDarkMode);
  }, [isDarkMode]);
  
  // Perform an additional check on mount to ensure correct initial state
  useEffect(() => {
    // Force-check dark mode on mount
    const checkDarkMode = () => {
      const hasDarkClass = document.documentElement.classList.contains('dark');
      setIsDark(hasDarkClass);
    };
    
    checkDarkMode();
    
    // Also check again after a short delay to catch any theme system initialization
    const timer = setTimeout(checkDarkMode, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Function for smooth scrolling to sections
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (!element) return;

    const offset = 20;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition + offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  // Determine which logo to show
  const logoSrc = isDark ? "/images/logos/logo-big-white.png" : "/images/logos/logo-big-colored.png";

  return (
    <footer className="relative bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 bottom-0 -z-10 -translate-x-1/2 blur-3xl" aria-hidden="true">
          <div
            className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-purple-200 to-indigo-300 opacity-10 dark:from-purple-800 dark:to-indigo-900 dark:opacity-20"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="space-y-8 xl:col-span-1">
            <Link to="/" className="flex items-center">
              <img 
                key={`logo-${isDark ? 'dark' : 'light'}`}
                src={logoSrc}
                alt="Advocacy Lab"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-sm leading-6 text-gray-600 dark:text-gray-400">
              Creating high-impact political creative for campaigns, offices, and advocacy groups.
            </p>
            <div className="flex space-x-6">
              {navigation.social.map((item) => (
                <a key={item.name} href={item.href} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          
          <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div>
              <h3 className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Product
              </h3>
              <ul role="list" className="mt-6 space-y-4">
                {navigation.product.map((item) => (
                  <li key={item.name}>
                    {item.href.startsWith('#') ? (
                      <a
                        href={item.href}
                        onClick={(e) => scrollToSection(e, item.href.substring(1))}
                        className="text-sm leading-6 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link to={item.href} className="text-sm leading-6 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Company
              </h3>
              <ul role="list" className="mt-6 space-y-4">
                {navigation.company.map((item) => (
                  <li key={item.name}>
                    {item.href.startsWith('#') ? (
                      <a
                        href={item.href}
                        onClick={(e) => scrollToSection(e, item.href.substring(1))}
                        className="text-sm leading-6 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link to={item.href} className="text-sm leading-6 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-16 border-t border-gray-200 dark:border-gray-800 pt-8 pb-2">
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
            Powered by &copy; {new Date().getFullYear()} FullPAC, Inc.
          </p>
        </div>
      </div>
    </footer>
  );
}